import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { FormModal } from '@/components/ui/form-modal';
import { useResourceSets } from 'components/IAP/hooks/resourceSetHooks';
import {
  useAttachResourceToSeat,
  useGetPositionsForIncident,
  useGetRosterForIncident,
  useGetSeatResourcesForRoster,
} from 'hooks/incident/useRoster';
import {
  SearchableSelect,
  type SearchableSelectOption,
} from './SearchableSelect';

type AttachResourceToSeatDialogProps = {
  show: boolean;
  onClose: () => void;
  positionId: string;
  positionName: string;
  isDraft: boolean;
  /** Template charts keep the pick locally instead of writing an incident seat. */
  onPick?: (pick: {
    resourceSetId: string;
    name: string;
    pointOfContactUserId?: string | null;
  }) => void;
};

const AttachResourceToSeatDialog = ({
  show,
  onClose,
  positionId,
  positionName,
  isDraft,
  onPick,
}: AttachResourceToSeatDialogProps) => {
  const { data: resourceSets = [] } = useResourceSets();
  const { data: positions = [] } = useGetPositionsForIncident();
  const { data: rosterEntries = [] } = useGetRosterForIncident();
  const { data: seatResources = [] } = useGetSeatResourcesForRoster(isDraft);

  const { mutate: attachResource, isPending } = useAttachResourceToSeat();

  const [selectedResource, setSelectedResource] =
    useState<SearchableSelectOption | null>(null);
  const [selectedPointOfContact, setSelectedPointOfContact] =
    useState<SearchableSelectOption | null>(null);
  const [showValidation, setShowValidation] = useState(false);

  const resourceOptions = useMemo(() => {
    // A resource can only sit in one place at a time, so anything already attached to a seat on this roster or standing
    // on the chart as its own node is filtered out rather than offered and rejected by the server.
    const alreadyPlaced = new Set<string>([
      ...seatResources.map((seatResource) => seatResource.resourceSetId),
      ...positions
        .filter((position) => position.isResourcePosition)
        .map((position) => position.resourceSetId)
        .filter((resourceSetId): resourceSetId is string => !!resourceSetId),
    ]);

    return resourceSets
      .filter(
        (resourceSet) =>
          resourceSet.kind !== 'Personnel' &&
          !resourceSet.archived &&
          !alreadyPlaced.has(resourceSet.resource_set_id)
      )
      .map((resourceSet) => ({
        value: resourceSet.resource_set_id,
        label: `${resourceSet.name}${resourceSet.kind ? ` — ${resourceSet.kind}` : ''}`,
      }));
  }, [resourceSets, positions, seatResources]);

  const pointOfContactOptions = useMemo(
    () =>
      rosterEntries.map((entry) => ({
        value: entry.userId,
        label: entry.userName ?? entry.emailAddress ?? entry.userId,
      })),
    [rosterEntries]
  );

  const close = () => {
    setSelectedResource(null);
    setSelectedPointOfContact(null);
    setShowValidation(false);
    onClose();
  };

  const onSubmit = () => {
    if (!selectedResource || (!onPick && !selectedPointOfContact)) {
      setShowValidation(true);
      return;
    }
    if (onPick) {
      const resourceSet = resourceSets.find(
        (set) => set.resource_set_id === String(selectedResource.value)
      );
      const pointOfContact = resourceSet?.point_of_contact;
      const pointOfContactUserId =
        typeof pointOfContact === 'string'
          ? pointOfContact
          : pointOfContact && typeof pointOfContact === 'object'
            ? String(
                (pointOfContact as { user_guid?: string; userGuid?: string })
                  .user_guid ??
                  (pointOfContact as { userGuid?: string }).userGuid ??
                  ''
              )
            : '';
      onPick({
        resourceSetId: String(selectedResource.value),
        name: selectedResource.label,
        pointOfContactUserId: pointOfContactUserId || null,
      });
      close();
      return;
    }
    if (!selectedPointOfContact) return;

    attachResource(
      {
        positionId,
        resourceSetId: String(selectedResource.value),
        pointOfContactUserId: String(selectedPointOfContact.value),
        isDraft,
      },
      {
        onSuccess: () => {
          toast.success('Resource attached to seat');
          close();
        },
      }
    );
  };

  const missing = (selected: SearchableSelectOption | null) =>
    showValidation && !selected;

  return (
    <FormModal
      open={show}
      onOpenChange={(open) => {
        if (!open) close();
      }}
      title={`Attach ${isDraft ? '(Draft) ' : ''}Resource to ${positionName}`}
      formProps={{
        onSubmit: (event) => {
          event.preventDefault();
          onSubmit();
        },
      }}
      footer={
        <>
          <Button type="button" variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button type="submit" disabled={isPending}>
            Attach Resource
          </Button>
        </>
      }
    >
      <div className="tw:space-y-4">
        <div>
          <SearchableSelect
            id="attach-seat-resource"
            label="Resource"
            options={resourceOptions}
            value={selectedResource}
            onChange={setSelectedResource}
            placeholder="Select a resource"
            clearable
            invalid={missing(selectedResource)}
          />
          {missing(selectedResource) ? (
            <p className="tw:mt-1 tw:text-sm tw:text-destructive">
              A resource is required
            </p>
          ) : null}
        </div>

        {!onPick ? (
          <div>
            <SearchableSelect
              id="attach-seat-resource-point-of-contact"
              label="Point of Contact"
              options={pointOfContactOptions}
              value={selectedPointOfContact}
              onChange={setSelectedPointOfContact}
              placeholder="Select a point of contact"
              clearable
              invalid={missing(selectedPointOfContact)}
            />
            {missing(selectedPointOfContact) ? (
              <p className="tw:mt-1 tw:text-sm tw:text-destructive">
                A point of contact is required
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </FormModal>
  );
};

export default AttachResourceToSeatDialog;
